import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractCrudService } from '../../shared/abstract-crud.service';
import { NotificationEntry } from './notification.model';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { WebsocketMessage } from '../../shared/websocket/websocketMessage';
import { _ } from '../../shared/utils';
import { AppMessage } from '../../shared/app-message/app-message';
import { WebsocketService } from '../../shared/websocket/websocket.service';
import { TranslateService } from '@ngx-translate/core';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { Comment } from './comment/comment.model';
import { AuthService } from '~app/auth/auth.service';

/**
 * This service is used to display confirm dialogs when requested.
 */
@Injectable()
export class NotificationService extends AbstractCrudService<NotificationEntry> {
  /**
   * Websocket message stream
   * @type {Subject<any>}
   */
  private websocketMessage = new BehaviorSubject<WebsocketMessage>(new WebsocketMessage());
  /**
   * Observable for new websocket messages
   * @type {Observable<any>}
   */
  public newWebsocketMessage$ = this.websocketMessage.asObservable();
  /**
   * URL for current model, override url from superclass
   * @type {string}
   */
  protected url = environment.regoApiURL + 'notifications';
  /**
   * ID of active notification
   */
  private activeId: number | null;

  /**
   * Default constructor
   * @param {HttpClient} http
   * @param {WebsocketService} websocket
   * @param {TranslateService} translate
   * @param {AppMessageService} appMessageService
   */
  constructor(
    http: HttpClient,
    private websocket: WebsocketService,
    private translate: TranslateService,
    private auth: AuthService,
    private appMessageService: AppMessageService) {
    super(http);
    this.websocket.newWebsocketMessage$.subscribe(data => {
      if (data.target === 'notification') {
        this.websocketMessage.next(data);
        if (data.type === 'add') {
          const notification: NotificationEntry = data.body;
          if (notification.userId !== auth.getSubject()) {
            this.appMessageService.broadcastNotification(notification);
          }
        }
      } else if (data.target === 'notification_comment') {
        if (data.type === 'add') {
          const comment: Comment = data.body;
          if (comment.userId !== auth.getSubject()) {
            this.appMessageService.broadcastNotificationComment(comment);
          }
        }
      }
    });
  }

  public getMessageStatistics(start: string, end: string) {
    return this.http.get(this.url + '?withDeletedAt=false&createdAt_lte=' + start + '&createdAt_gte=' + end).toPromise();
  }

  public jsonFilter() {
    return this.http.get('https://api-rb-develop.miprorego.io/rego/v1/notifications?mapObjectId_like=vr_%&createdAt_lte=2018-04-17&createdAt_gte=2018-04-10').toPromise();
  }

  public searchResults(query: string): Promise<NotificationEntry[]> {
    return this.list().then(serviceAnswer => {
      if (serviceAnswer.isOk) {
        return serviceAnswer.data.filter(notification => notification.description.toLowerCase().includes(query.toLowerCase()));
      }
      return [];
    });
  }

  public getActive() {
    return this.activeId;
  }

  public setActive(id: number) {
    this.activeId = id;
  }
}
